// src/services/formService.impl.js - standardized implementation using the service factory
import { 
  createService, 
  createNestedCollection
} from './factory';
import { objectToQueryString } from '../utils/apiUtils';
import { api } from './api';

// Create the base form service using the factory
const formServiceBase = createService('Form', '/forms', {
  // Nested collections
  nestedCollections: [
    createNestedCollection('submissions', 'submission'),
    createNestedCollection('shares', 'share')
  ],
  // Custom endpoints that don't fit the standard patterns
  customEndpoints: {
    // Form Definitions
    getFormDefinitions: async (params = {}) => {
      return api.get(`/forms/definitions${objectToQueryString(params)}`);
    },
    
    getFormDefinitionById: async (formId) => {
      return api.get(`/forms/definitions/${formId}`);
    },
    
    createFormDefinition: async (formData) => {
      return api.post('/forms/definitions', formData);
    },
    
    updateFormDefinition: async (formId, formData) => {
      return api.put(`/forms/definitions/${formId}`, formData);
    },
    
    deleteFormDefinition: async (formId) => {
      return api.delete(`/forms/definitions/${formId}`);
    },
    
    // Submissions
    getFormSubmissions: async (formId, params = {}) => {
      const queryParams = { ...params, formDefinition: formId };
      return api.get(`/forms/submissions${objectToQueryString(queryParams)}`);
    },
    
    getSubmissionById: async (formId, submissionId) => {
      return api.get(`/forms/submissions/${submissionId}${objectToQueryString({ formDefinition: formId })}`);
    },
    
    submitForm: async (formId, submissionData) => {
      const data = { ...submissionData, formDefinition: formId };
      return api.post('/forms/submissions', data);
    },
    
    updateSubmissionStatus: async (formId, submissionId, statusData) => {
      return api.put(`/forms/submissions/${submissionId}`, {
        ...statusData,
        formDefinition: formId,
        updateType: 'status'
      });
    },
    
    // Shared forms
    getSharedForm: async (shareToken, options = {}) => {
      return api.get(`/forms/public/${shareToken}${objectToQueryString(options)}`);
    },
    
    submitSharedForm: async (shareToken, submissionData) => {
      return api.post(`/forms/public/${shareToken}/submit`, submissionData);
    },
    
    verifyFormPassword: async (shareToken, password) => {
      return api.post(`/forms/public/${shareToken}/verify`, { password });
    },
    
    // Sharing
    shareForm: async (formId, shareOptions = {}) => {
      return api.post(`/forms/definitions/${formId}/shares`, shareOptions);
    },
    
    getFormShares: async (formId) => {
      return api.get(`/forms/definitions/${formId}/shares`);
    },
    
    updateFormShare: async (shareId, shareOptions) => {
      return api.put(`/forms/shares/${shareId}`, shareOptions);
    },
    
    deleteFormShare: async (shareId) => {
      return api.delete(`/forms/shares/${shareId}`);
    },
    
    // File uploads
    getSubmissionFileUploadUrl: async (submissionId, fileInfo) => {
      return api.post('/forms/submissions/upload-url', {
        ...fileInfo,
        submissionId
      });
    },
    
    getPublicSubmissionFileUploadUrl: async (shareToken, submissionId, fileInfo) => {
      return api.post(`/forms/public/${shareToken}/submit/upload-url`, {
        ...fileInfo,
        submissionId
      });
    },
    
    // Analytics and reports
    exportFormSubmissions: async (formId, exportOptions = {}) => {
      return api.post(`/forms/report/${formId}`, {
        ...exportOptions,
        format: exportOptions.format || 'csv'
      }, { responseType: 'blob' });
    },
    
    getFormAnalytics: async (formId, params = {}) => {
      return api.get(`/forms/analytics/${formId}${objectToQueryString(params)}`);
    }
  }
});

// Create backward-compatible API for existing formService
// These will extract the actual data from the standardized response
export const getForms = async (params = {}) => {
  return await formServiceBase.getFormDefinitions(params);
};

export const getFormById = async (formId) => {
  return await formServiceBase.getFormDefinitionById(formId);
};

export const createForm = async (formData) => {
  return await formServiceBase.createFormDefinition(formData);
};

export const updateForm = async (formId, formData) => {
  return await formServiceBase.updateFormDefinition(formId, formData);
};

export const deleteForm = async (formId) => {
  return await formServiceBase.deleteFormDefinition(formId);
};

export const getFormSubmissions = async (formId, params = {}) => {
  const response = await formServiceBase.getFormSubmissions(formId, params);
  
  // Extract the submissions array with proper fallbacks
  const submissions = 
    response.data?.formSubmissions || 
    response.formSubmissions || 
    response.submissions || 
    [];
  
  // Extract the total count with proper fallbacks
  const total = 
    response.meta?.total || 
    response.data?.meta?.total ||
    response.total || 
    (response.data?.meta && typeof response.data.meta.total === 'number' ? response.data.meta.total : null) ||
    submissions.length || 
    0;
  
  // Log the extracted values for debugging
  console.log('Submissions response:', { 
    rawResponse: response,
    extractedSubmissions: submissions.length,
    extractedTotal: total 
  });
  
  return {
    submissions,
    total,
    meta: response.meta || response.data?.meta || {},
    ...response
  };
};

export const getSubmissionById = async (formId, submissionId) => {
  const response = await formServiceBase.getSubmissionById(formId, submissionId);
  
  // Extract submission with proper fallbacks
  const submission = response.data?.formSubmission || response.formSubmission || response.submission || response;
  
  // Normalize the submission data structure
  const normalizedSubmission = {
    ...submission,
    // Ensure formData exists and is properly structured
    data: submission.data || {},
    // Handle different ways the form definition might be included
    formDefinition: submission.formDefinition || response.data?.formDefinition || response.formDefinition,
  };
  
  return {
    submission: normalizedSubmission,
    form: response.form || response.data?.formDefinition || response.formDefinition || {},
    ...response
  };
};

export const submitForm = async (formId, submissionData) => {
  const response = await formServiceBase.submitForm(formId, submissionData);
  return {
    submission: response.formSubmission || response.submission || response,
    ...response
  };
};

export const deleteSubmission = async (formId, submissionId) => {
  // The API endpoint is /forms/submissions/:id (not nested under form ID)
  return api.delete(`/forms/submissions/${submissionId}`);
};

export const submitSharedForm = async (shareToken, submissionData) => {
  // Convert 'en-US' to 'en' for language field to match server-side enum
  if (submissionData.language === 'en-US') {
    submissionData.language = 'en';
  }
  if (submissionData.metadata?.language === 'en-US') {
    submissionData.metadata.language = 'en';
  }
  
  const response = await formServiceBase.submitSharedForm(shareToken, submissionData);
  return {
    submission: response.formSubmission || response.submission || response,
    ...response
  };
};

export const getSharedForm = async (shareToken, options = {}) => {
  const response = await formServiceBase.getSharedForm(shareToken, options);
  return {
    form: response.form || response.formDefinition || response,
    ...response
  };
};

export const verifyFormPassword = async (shareToken, password) => {
  return formServiceBase.verifyFormPassword(shareToken, password);
};

export const shareForm = async (formId, shareOptions = {}) => {
  try {
    const response = await formServiceBase.shareForm(formId, shareOptions);
    
    if (!response) {
      throw new Error('No response from server');
    }
    
    // Extract share with proper fallbacks
    return {
      share: response.share || response.data?.share || response,
      ...response
    };
  } catch (error) {
    console.error('Error sharing form:', error);
    throw error;
  }
};

export const getFormShares = async (formId) => {
  try {
    const response = await formServiceBase.getFormShares(formId);
    
    if (!response) {
      return { shares: [] };
    }
    
    // Extract shares with proper fallbacks
    return {
      shares: response.shares || response.data?.shares || [],
      ...response
    };
  } catch (error) {
    console.error('Error fetching form shares:', error);
    throw error;
  }
};

export const updateFormShare = async (formId, shareId, shareOptions) => {
  try {
    // Make sure we handle status correctly
    const payload = { ...shareOptions };
    
    // If isActive exists but status doesn't, map isActive to status
    if (payload.isActive !== undefined && payload.status === undefined) {
      payload.status = payload.isActive ? 'active' : 'disabled';
      // Remove isActive as the server doesn't expect it
      delete payload.isActive;
    }
    
    const response = await formServiceBase.updateFormShare(shareId, payload);
    
    if (!response) {
      throw new Error('No response from server');
    }
    
    console.log('Form share update response:', response);
    
    // Extract the share data with proper fallbacks
    const result = {
      share: response.share || response.data?.share || response,
      ...response
    };
    
    // Map status to isActive for backward compatibility
    if (result.share) {
      result.share.isActive = result.share.status === 'active';
    }
    
    // Return the processed result
    
    return result;
  } catch (error) {
    console.error('Error updating form share:', error);
    throw error;
  }
};

export const deleteFormShare = async (formId, shareId) => {
  try {
    const response = await formServiceBase.deleteFormShare(shareId);
    return response;
  } catch (error) {
    console.error('Error deleting form share:', error);
    throw error;
  }
};

export const updateSubmissionStatus = async (formId, submissionId, statusData) => {
  const response = await formServiceBase.updateSubmissionStatus(formId, submissionId, statusData);
  return {
    submission: response.formSubmission || response.submission || response,
    ...response
  };
};

export const getSubmissionFileUploadUrl = async (submissionId, fileInfo) => {
  return formServiceBase.getSubmissionFileUploadUrl(submissionId, fileInfo);
};

export const getPublicSubmissionFileUploadUrl = async (shareToken, submissionId, fileInfo) => {
  return formServiceBase.getPublicSubmissionFileUploadUrl(shareToken, submissionId, fileInfo);
};

export const exportFormSubmissions = async (formId, exportOptions = {}) => {
  return formServiceBase.exportFormSubmissions(formId, exportOptions);
};

export const getFormAnalytics = async (formId, params = {}) => {
  const response = await formServiceBase.getFormAnalytics(formId, params);
  return {
    analytics: response.analytics || response,
    ...response
  };
};

// Export complete service object for backwards compatibility
const formService = {
  getForms,
  getFormById,
  createForm,
  updateForm,
  deleteForm,
  getFormSubmissions,
  getSubmissionById,
  submitForm,
  submitSharedForm,
  getSharedForm,
  verifyFormPassword,
  shareForm,
  getFormShares,
  updateFormShare,
  deleteFormShare,
  updateSubmissionStatus,
  getSubmissionFileUploadUrl,
  getPublicSubmissionFileUploadUrl,
  exportFormSubmissions,
  getFormAnalytics
};

export default formService;